import * as React from "react";

import { ShellActionsSlot } from "../shell-title-context";
import { Masthead } from "./masthead";
import { type MetaFact } from "./meta-facts";
import { type NavTab } from "../nav-tabs";

export interface SectionTitleBarProps {
  /** The scope's name — rendered in the brand serif display face (28px). */
  title: React.ReactNode;
  /** Optional one-line description under the title (what the surface is FOR) —
   *  the same masthead slot PageHeader carries, so a mode root reads like every
   *  other page. Muted, type-body. See docs/design/PAGE-HEADER-UNIFICATION-PRD.md. */
  description?: React.ReactNode;
  /** The "← Parent" back eyebrow above the title (e.g. "← Work" on a section). */
  back?: { label: string; href?: string; onClick?: () => void };
  /** Icon meta-facts under the description (Scope · Total · Live…) — the same
   *  strip the workflow detail carries, so a mode surface can state its shape. */
  meta?: MetaFact[];
  /** Optional tiny letterspaced eyebrow under the title (e.g. the mode's question). */
  eyebrow?: React.ReactNode;
  /**
   * Right-aligned actions inline on the title row — the scope's own primary CTA
   * + filters + view switch. Injected by the page; this component owns none of them.
   */
  actions?: React.ReactNode;
  /** Optional in-page sub-nav below the title (the section's own status/view
   *  pills), rendered through the canonical NavTabs. */
  tabs?: NavTab[];
  /** Controlled-mode selection for `tabs` (local state → buttons). */
  navValue?: string;
  onNavSelect?: (key: string) => void;
  /** Slot to the right of `tabs` (a report-range select, a kbd hint…). */
  navRight?: React.ReactNode;
  className?: string;
}

/**
 * SectionTitleBar — the mode-shell title treatment (Matt, 2026-07-16): the
 * scope's name in the brand serif display face over the shared `<Masthead>`, so a
 * mode page carries the SAME back eyebrow, description, icon meta-facts, and pill
 * sub-nav as `/styleguide/workflow-detail` and every standalone `PageHeader`
 * (docs/design/PAGE-HEADER-UNIFICATION-PRD.md).
 *
 * Deliberately **pulse-independent**: the title + its injected actions are their
 * own component, decoupled from the Pulse. Whatever the Pulse does — render a wall,
 * a strip, or nothing at all — the title and its CTA/filters still render. Drop it
 * at the top of ANY mode page and inject the page's actions.
 */
export function SectionTitleBar({
  title,
  description,
  back,
  meta,
  eyebrow,
  actions,
  tabs,
  navValue,
  onNavSelect,
  navRight,
  className,
}: SectionTitleBarProps) {
  return (
    <Masthead
      className={className}
      back={back}
      eyebrow={eyebrow}
      title={title}
      description={description}
      meta={meta}
      tabs={tabs}
      navValue={navValue}
      onNavSelect={onNavSelect}
      navRight={navRight}
      // The producer-supplied `actions` (e.g. Work▸Issues) render first; the
      // <ShellActionsSlot> is the live portal target where a mounted page's own
      // PageHeader toolbar lifts up (hubs, CRM/ATS views, …). Always rendered so
      // the slot exists even when this section has no producer actions.
      actions={
        <div className="flex flex-wrap items-center gap-2 empty:hidden">
          {actions}
          <ShellActionsSlot />
        </div>
      }
    />
  );
}
